import React, { useState } from 'react';
import { Pencil, Trash2, Check, X } from 'lucide-react';

// ============================================================
// CONVERSATION VIEW
// Renders a lead's conversation. Two modes:
//   - structured `messages` rows (Phase 2): chat bubbles with per-message
//     local-timezone timestamps, and inline edit/delete when handlers + canEdit
//     are passed.
//   - raw `text` (Upwork referral page, free-form notes): parsed to bubbles,
//     falling back to plain text. Read-only.
// ============================================================

// Each turn: { speaker: 'BDE' | 'Prospect', meta: string, text: string }
export const parseConversation = (text) => {
  if (!text) return { parsed: false, turns: [] };
  const lines = text.split(/\r?\n/);
  const turns = [];
  let current = null;
  const labelRegex = /^(BDE|Prospect)(\s*(?:\[[^\]]+\]|\([^)]+\)))?\s*:\s*(.*)$/i;

  for (const line of lines) {
    const m = line.match(labelRegex);
    if (m) {
      if (current) turns.push(current);
      current = {
        speaker: m[1].toUpperCase() === 'BDE' ? 'BDE' : 'Prospect',
        meta: m[2] || '',
        text: m[3]
      };
    } else if (current) {
      current.text += (current.text ? '\n' : '') + line;
    }
  }
  if (current) turns.push(current);
  turns.forEach(t => { t.text = t.text.trim(); });
  return { parsed: turns.length > 0, turns };
};

// Format an ISO-UTC timestamp into the VIEWER's local timezone, 12-hour.
// Returns '' for null/unparseable (un-timestamped history).
// Exported so LeadDetail's AI Workspace context uses the exact same rendering
// the BDE sees on screen — the copied text should not disagree with the page.
export const formatLocalTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  try {
    return d.toLocaleString(undefined, {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch {
    return d.toLocaleString();
  }
};

const bubbleWrap = (isBDE) => ({
  maxWidth: '75%',
  background: isBDE ? '#EFF6FF' : '#F3F4F6',
  border: `1px solid ${isBDE ? '#BFDBFE' : '#E5E7EB'}`,
  borderRadius: 8,
  padding: '10px 14px',
});

// Read-only bubble (text/parsed path).
const Bubble = ({ turn }) => {
  const isBDE = turn.speaker === 'BDE';
  return (
    <div style={{ display: 'flex', justifyContent: isBDE ? 'flex-start' : 'flex-end', marginBottom: 12 }}>
      <div style={bubbleWrap(isBDE)}>
        <div style={{ fontSize: 11, fontWeight: 600, color: isBDE ? '#1D4ED8' : '#374151', marginBottom: 4 }}>
          {turn.speaker}{turn.meta ? ` ${turn.meta}` : ''}
        </div>
        <div style={{ fontSize: 14, color: '#111827', whiteSpace: 'pre-wrap' }}>{turn.text}</div>
      </div>
    </div>
  );
};

// Structured-message bubble with optional inline edit/delete.
const MessageBubble = ({ msg, canEdit, onEdit, onDelete }) => {
  const isBDE = msg.sender === 'bde';
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(msg.body);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    const ok = await onEdit(msg.id, draft.trim());
    setBusy(false);
    if (ok) setEditing(false);
  };

  const time = formatLocalTime(msg.created_at);
  return (
    <div style={{ display: 'flex', justifyContent: isBDE ? 'flex-start' : 'flex-end', marginBottom: 12 }}>
      <div style={bubbleWrap(isBDE)}>
        <div style={{ fontSize: 11, fontWeight: 600, color: isBDE ? '#1D4ED8' : '#374151', marginBottom: 4, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
          <span>{isBDE ? 'BDE' : 'Prospect'}{msg.meta ? ` ${msg.meta}` : ''}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {time ? <span style={{ fontWeight: 400, color: '#9CA3AF' }}>{time}</span> : null}
            {canEdit && !editing && (
              <>
                <button onClick={() => { setDraft(msg.body); setEditing(true); }} title="Edit"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 0, display: 'inline-flex' }}>
                  <Pencil size={12} />
                </button>
                <button onClick={() => { if (window.confirm('Delete this message?')) onDelete(msg.id); }} title="Delete"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 0, display: 'inline-flex' }}>
                  <Trash2 size={12} />
                </button>
              </>
            )}
          </span>
        </div>
        {editing ? (
          <div>
            <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3}
              style={{ width: '100%', fontSize: 14, padding: '6px 8px', border: '1px solid #D1D5DB', borderRadius: 4 }} />
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <button onClick={save} disabled={busy} className="btn btn-primary" style={{ padding: '3px 10px', fontSize: 12 }}>
                <Check size={12} /> {busy ? '…' : 'Save'}
              </button>
              <button onClick={() => setEditing(false)} disabled={busy} className="btn btn-secondary" style={{ padding: '3px 10px', fontSize: 12 }}>
                <X size={12} /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 14, color: '#111827', whiteSpace: 'pre-wrap' }}>{msg.body}</div>
        )}
      </div>
    </div>
  );
};

const ConversationView = ({ text, messages, onEditMessage, onDeleteMessage, canEdit = false }) => {
  // Structured path (Phase 2).
  if (Array.isArray(messages) && messages.length > 0) {
    return (
      <div>
        {messages.map((m) => (
          <MessageBubble
            key={m.id}
            msg={m}
            canEdit={canEdit}
            onEdit={onEditMessage}
            onDelete={onDeleteMessage}
          />
        ))}
      </div>
    );
  }

  const { parsed, turns } = parseConversation(text);
  if (!parsed) {
    return (
      <pre style={{
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        background: '#F9FAFB',
        border: '1px solid #E5E7EB',
        borderRadius: 8,
        padding: 12,
        fontSize: 13,
        color: '#374151',
        fontFamily: 'inherit',
      }}>{text || '(No conversation logged.)'}</pre>
    );
  }
  return <div>{turns.map((t, i) => <Bubble key={i} turn={t} />)}</div>;
};

export default ConversationView;
